import { MoveRight } from "lucide-react";
import {
  RecordContextProvider,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState, type CSSProperties, type DragEvent } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { contactFullName } from "../contacts/contactModel";
import type {
  Contact,
  Sponsoring,
  SponsoringNote,
  SponsoringStage,
} from "../types";
import { useDashboardFilters } from "./useDashboardFilters";

/**
 * The lead funnel: one column per lead stage with a card per lead. On a
 * desktop the columns share one grid row and cards can be dragged to another
 * column (native HTML drag and drop, no library). On a phone the columns
 * scroll horizontally with scroll snapping and every card carries a
 * "Verplaats naar…" menu, because dragging does not work on touch. Both paths
 * update the sponsoring's stage.
 */
export const LeadsBoard = ({
  onAddLead,
}: {
  /** Called with the stage value when "+ lead" of a column is clicked */
  onAddLead: (stage: string) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { boardStages, filters } = useDashboardFilters();
  const { data: leads = [], isPending } = useGetList<Sponsoring>(
    "sponsorings",
    {
      filter: filters.openLeads,
      sort: { field: "updated_at", order: "DESC" },
      pagination: { page: 1, perPage: 200 },
    },
  );
  const [update] = useUpdate();
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  const moveLead = (leadId: string, stage: string) => {
    const lead = leads.find((candidate) => String(candidate.id) === leadId);
    if (!lead || lead.stage === stage) return;
    update(
      "sponsorings",
      { id: lead.id, data: { stage }, previousData: lead },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("resources.sponsorings.updated", { type: "info" }),
        onError: () => notify("ra.notification.http_error", { type: "error" }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3" data-testid="leads-board">
      <h2 className="text-lg font-bold text-brand-navy">
        {translate("crm.dashboard.funnel.title")}
      </h2>
      <div
        className="flex snap-x snap-mandatory items-start gap-4 overflow-x-auto pb-2 md:grid md:snap-none md:items-stretch md:overflow-x-visible md:pb-0 md:[grid-template-columns:repeat(var(--funnel-columns),minmax(0,1fr))]"
        style={{ "--funnel-columns": boardStages.length } as CSSProperties}
      >
        {boardStages.map((stage) => (
          <LeadColumn
            key={stage.value}
            stage={stage}
            stages={boardStages}
            leads={leads.filter((lead) => lead.stage === stage.value)}
            isPending={isPending}
            isDragOver={dragOverStage === stage.value}
            onDragOver={() => setDragOverStage(stage.value)}
            onDragLeave={() => setDragOverStage(null)}
            onDrop={(leadId) => {
              setDragOverStage(null);
              moveLead(leadId, stage.value);
            }}
            onMoveLead={moveLead}
            onAddLead={() => onAddLead(stage.value)}
          />
        ))}
      </div>
    </div>
  );
};

const LeadColumn = ({
  stage,
  stages,
  leads,
  isPending,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onMoveLead,
  onAddLead,
}: {
  stage: SponsoringStage;
  /** Every funnel stage, for the "Verplaats naar…" menu of the cards */
  stages: SponsoringStage[];
  leads: Sponsoring[];
  isPending: boolean;
  isDragOver: boolean;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (leadId: string) => void;
  onMoveLead: (leadId: string, stage: string) => void;
  onAddLead: () => void;
}) => {
  const translate = useTranslate();

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    onDragOver();
  };
  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    onDrop(event.dataTransfer.getData("text/plain"));
  };

  return (
    <section
      className={cn(
        "flex min-h-64 w-72 shrink-0 snap-start flex-col rounded-xl border border-border bg-white p-3 transition-shadow md:w-auto",
        isDragOver && "ring-2 ring-brand-blue/40",
      )}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      aria-label={stage.label}
      data-testid={`leads-column-${stage.value}`}
    >
      <header className="mb-3 flex items-center justify-between border-b-2 border-brand-blue pb-2">
        <h3 className="truncate text-xs font-semibold tracking-wider text-brand-navy uppercase">
          {stage.label}
        </h3>
        <span
          className="ml-2 shrink-0 rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-semibold text-brand-navy tabular-nums"
          data-testid={`leads-column-${stage.value}-count`}
        >
          {leads.length}
        </span>
      </header>
      <ul className="flex flex-1 flex-col gap-2">
        {isPending ? (
          <li>
            <Skeleton className="h-16 w-full rounded-lg" />
          </li>
        ) : (
          leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              stages={stages}
              onMoveLead={onMoveLead}
            />
          ))
        )}
      </ul>
      <button
        type="button"
        onClick={onAddLead}
        className="mt-3 flex h-9 w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-brand-blue hover:text-brand-navy"
      >
        + {translate("crm.dashboard.funnel.add_lead")}
      </button>
    </section>
  );
};

const LeadCard = ({
  lead,
  stages,
  onMoveLead,
}: {
  lead: Sponsoring;
  stages: SponsoringStage[];
  onMoveLead: (leadId: string, stage: string) => void;
}) => {
  const translate = useTranslate();
  const { data: contacts } = useGetList<Contact>("contacts", {
    filter: { company_id: lead.company_id },
    sort: { field: "id", order: "ASC" },
    pagination: { page: 1, perPage: 1 },
  });
  const { data: notes } = useGetList<SponsoringNote>(
    "sponsoring_notes",
    {
      filter: { sponsoring_id: lead.id },
      sort: { field: "date", order: "DESC" },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !lead.description },
  );
  const contact = contacts?.[0];
  const snippet = lead.description || notes?.[0]?.text;
  const otherStages = stages.filter((stage) => stage.value !== lead.stage);

  const handleDragStart = (event: DragEvent) => {
    event.dataTransfer.setData("text/plain", String(lead.id));
    event.dataTransfer.effectAllowed = "move";
  };

  return (
    <li
      draggable
      onDragStart={handleDragStart}
      className="relative cursor-grab active:cursor-grabbing"
      data-testid={`lead-card-${lead.id}`}
    >
      <RecordContextProvider value={lead}>
        <Link
          to={`/sponsorings/${lead.id}/show`}
          className="block rounded-lg bg-muted/60 p-3 pr-10 text-foreground no-underline transition-colors hover:bg-muted"
        >
          <div className="truncate text-sm font-semibold text-brand-navy">
            <ReferenceField
              source="company_id"
              reference="companies"
              link={false}
              loading={<Skeleton className="h-4 w-24" />}
            />
          </div>
          {contact ? (
            <div className="truncate text-sm text-muted-foreground">
              {contactFullName(contact)}
            </div>
          ) : null}
          {snippet ? (
            <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
              {snippet}
            </p>
          ) : null}
        </Link>
      </RecordContextProvider>
      {otherStages.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="absolute top-2 right-2 flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-brand-blue/10 hover:text-brand-navy focus-visible:ring-2 focus-visible:ring-brand-blue/40 focus-visible:outline-none"
            aria-label={translate("crm.dashboard.funnel.move_lead")}
            data-testid={`lead-card-${lead.id}-move`}
          >
            <MoveRight className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              {translate("crm.dashboard.funnel.move_to")}
            </DropdownMenuLabel>
            {otherStages.map((stage) => (
              <DropdownMenuItem
                key={stage.value}
                onSelect={() => onMoveLead(String(lead.id), stage.value)}
              >
                {stage.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
};
